/**
 * Profile Page
 *
 * Allows users to view and update their profile information using Supabase.
 * Email is read-only (comes from Supabase Auth).
 * Name and phone are stored in the customers table.
 */

import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Link } from 'react-router-dom';
import {
  Container,
  Typography,
  Paper,
  Box,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Breadcrumbs
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

const Profile = () => {
  const { user, customer, updateProfile, error: authError } = useAuth();

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  // Initialize form with customer data (Supabase snake_case field names)
  useEffect(() => {
    if (customer) {
      setFirstName(customer.first_name || '');
      setLastName(customer.last_name || '');
      setPhone(customer.phone || '');
    }
  }, [customer]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess(false);
    setLoading(true);

    try {
      await updateProfile({ first_name: firstName, last_name: lastName, phone });
      setSuccess(true);
      window.scrollTo(0, 0);
    } catch (err) {
      setError(err.message || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="md" sx={{ my: 4 }}>
      <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
        <Link to="/account" style={{ textDecoration: 'none', color: '#8B4513' }}>
          My Account
        </Link>
        <Typography color="text.primary">Profile</Typography>
      </Breadcrumbs>

      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Button
          component={Link}
          to="/account"
          startIcon={<ArrowBackIcon />}
          sx={{ mr: 2, color: '#8B4513' }}
        >
          Back
        </Button>
        <Typography variant="h4" component="h1" sx={{ color: '#8B4513' }}>
          Edit Profile
        </Typography>
      </Box>

      <Paper elevation={2} sx={{ p: 3 }}>
        {success && (
          <Alert severity="success" sx={{ mb: 3 }}>
            Profile updated successfully!
          </Alert>
        )}

        {(error || authError) && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error || authError}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit} noValidate>
          <Typography variant="subtitle1" gutterBottom>
            Account Information
          </Typography>

          <TextField
            margin="normal"
            fullWidth
            id="email"
            label="Email Address"
            name="email"
            value={user?.email || ''}
            disabled
            helperText="Email cannot be changed"
          />

          <TextField
            margin="normal"
            fullWidth
            id="firstName"
            label="First Name"
            name="firstName"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
          />

          <TextField
            margin="normal"
            fullWidth
            id="lastName"
            label="Last Name"
            name="lastName"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
          />

          <TextField
            margin="normal"
            fullWidth
            id="phone"
            label="Phone Number"
            name="phone"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
          />

          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between' }}>
            <Button
              component={Link}
              to="/account"
              variant="outlined"
              sx={{
                color: '#8B4513',
                borderColor: '#8B4513',
                '&:hover': { borderColor: '#6d3610' }
              }}
            >
              Cancel
            </Button>

            <Button
              type="submit"
              variant="contained"
              disabled={loading}
              sx={{ bgcolor: '#8B4513', '&:hover': { bgcolor: '#6d3610' } }}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : 'Save Changes'}
            </Button>
          </Box>
        </Box>
      </Paper>

      <Paper elevation={2} sx={{ p: 3, mt: 3 }}>
        <Typography variant="subtitle1" gutterBottom>
          Password Management
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          Use the link below to reset your password via email.
        </Typography>
        <Button
          component={Link}
          to="/forgot-password"
          variant="outlined"
          sx={{
            color: '#8B4513',
            borderColor: '#8B4513',
            '&:hover': { borderColor: '#6d3610' }
          }}
        >
          Reset Password
        </Button>
      </Paper>
    </Container>
  );
};

export default Profile;
